import { pickBy } from 'lodash-es';
import { useRouter } from 'next/router';
import type { Route } from 'nextjs-routes';
import { useCallback } from 'react';

type RouterUrl = Parameters<ReturnType<typeof useRouter>['push']>[0];
/**
 * Extract the "pathname" and "query" from the url parameter
 * of router.push, since it accepts both objects and strings
 */
function parseRoute(url: RouterUrl): {
  pathname?: Route['pathname'];
  query?: Route['query'];
} {
  if (typeof url === 'string') {
    return {
      pathname: url,
    };
  }
  return url;
}

interface UseNavigateOptions {
  replace?: boolean;
}

/**
 * Use this hook instead of router.push directly to preserve UTM parameters
 */
export function useNavigate() {
  const router = useRouter();

  // useCallback used not for performance but to avoid retriggering useEffect when included inside a dependency array
  return useCallback(
    (url: RouterUrl, options: UseNavigateOptions = {}) => {
      const utmParamsInUrl = pickBy(router.query, (value, key) => key.startsWith('utm_'));

      const { pathname, query } = parseRoute(url);

      const queryWithUtmParams = {
        ...query,
        ...utmParamsInUrl,
      };

      return (options.replace ? router.replace : router.push)(
        pathname
          ? {
              pathname,
              query: queryWithUtmParams,
            }
          : {
              query: queryWithUtmParams,
            },
      );
    },
    [router],
  );
}
